import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import CheckThinIcon from "@/components/ui/icon/icon/check-thin-icon";
import ChevronDownThinIcon from "@/components/ui/icon/icon/chevron-down-thin-icon";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";

interface DropdownSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  defaultValue?: string;
  showDefaultOption?: boolean;
  showClearButton?: boolean;
  options: Array<{
    value: string;
    label: string;
    desc?: string;
    icon?: React.ReactNode;
    flag?: React.ReactNode;
    disabled?: boolean;
  }>;
  width?: string;
  className?: string;
  /** 下拉层（Portal Content）额外 class，用于嵌套在 Popover 等高层级浮层之上时提高 z-index */
  contentClassName?: string;
  disabled?: boolean;
  prefixIcon?: React.ReactNode;
}

export default function Dropdown({
  value,
  options,
  width,
  className,
  contentClassName,
  disabled = false,
  onValueChange,
  defaultValue,
  showDefaultOption = true,
  showClearButton = false,
  placeholder,
  prefixIcon
}: DropdownSelectProps) {
  // 添加状态来跟踪下拉框是否打开
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (defaultValue && onValueChange) {
      onValueChange(defaultValue);
    }
  };

  // 判断是否显示清除按钮
  const shouldShowClearButton = showClearButton && value && value !== defaultValue;

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      onOpenChange={setIsOpen}
    >
      <SelectPrimitive.Trigger
        className={cn(
          "group flex h-10 w-full items-center justify-between rounded-sm border border-border bg-background px-3 py-2 font-normal text-sm text-i-normal",
          "focus:outline-none",
          "hover:bg-muted transition-colors",
          className
        )}
        style={{
          width: width,
        }}
      >
        <div className="flex items-center gap-2">
          {prefixIcon || options.find((option) => option.value === value)?.icon}
          <SelectPrimitive.Value />
          {placeholder && !value && (
            <span className="text-sm font-normal text-[rgba(102,112,133,1)] -ml-2">{placeholder}</span>
          )}
          {shouldShowClearButton && (
            <button
              type="button"
              onPointerDown={handleClear}
              className="hidden group-hover:flex items-center justify-center w-4 h-4"
              title="Reset"
            >
              <X className="w-[14px] h-[14px] text-i-placeholder" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <SelectPrimitive.Icon asChild>
            <ChevronDownThinIcon
              className={cn(
                "transition-transform duration-150",
                isOpen && "rotate-180"
              )}
            />
          </SelectPrimitive.Icon>
        </div>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            "relative z-50 overflow-hidden rounded-sm border bg-popover text-popover-foreground shadow-md max-h-72",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2",
            "data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2",
            "data-[side=top]:slide-in-from-bottom-2",
            contentClassName,
          )}
          style={{
            width: width,
          }}
          position="popper"
          sideOffset={4}
          align="start"
          collisionPadding={10}
        >
          <SelectPrimitive.Viewport className="p-1 space-y-1">
            {options.map((option) => {
              return (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    "relative flex w-full h-10 select-none items-center rounded-sm text-sm cursor-pointer",
                    "outline-none transition-colors pl-2 pr-8",
                    // 默认把自动聚焦的背景去掉
                    "data-[highlighted]:bg-transparent",
                    // 如果这一项是当前选中的值 (state=checked)，显示背景色
                    // 这样下次打开时，选中的那一项依然会有明显的激活状态
                    "data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground",
                    // 鼠标悬停时，强制显示高亮 (使用 ! 确保优先级)
                    "hover:!bg-accent hover:!text-accent-foreground",
                    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                    option.value === defaultValue && !showDefaultOption && "overflow-hidden -mt-1 h-0"
                  )}
                >
                  <div className="flex flex-col gap-1 flex-1">
                    <div className="flex items-center">
                      {option.icon && <div className="mr-2">{option.icon}</div>}
                      <SelectPrimitive.ItemText className="font-normal">
                        <span>{option.label}</span>
                      </SelectPrimitive.ItemText>
                      {option.flag && <div className="ml-4">{option.flag}</div>}
                    </div>
                    {option.desc && (
                      <div className="text-xs text-muted-foreground">
                        <span>{option.desc}</span>
                      </div>
                    )}
                  </div>

                  <span className="flex absolute right-2.5 h-4 w-4 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <CheckThinIcon className="w-4" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                </SelectPrimitive.Item>
              );
            })}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
